import { z } from 'zod'

/**
 * What the booking function reports about the booker's Business Portal access.
 * Comes from litta-api after the booking exists.
 */
export const BookerCheckSchema = z.object({
    email: z.string(),
    failed: z.boolean().optional(),
    outcome: z.enum([
        'NEW_USER',
        'EXISTING_OTHER_ACCOUNT',
        'ALREADY_ON_ACCOUNT',
        'NOT_ELIGIBLE',
    ]),
})

export type BookerCheck = z.infer<typeof BookerCheckSchema>

export interface BookerPortalMessage {
    severity: 'info' | 'warn'
    text: string
}

/**
 * The line to show under the booking confirmation, or null for nothing extra.
 * Never throws: a missing or malformed result simply shows nothing, so it can
 * never get in the way of the confirmation.
 */
export function bookerPortalMessage(
    check: unknown,
): BookerPortalMessage | null {
    const parsed = BookerCheckSchema.safeParse(check)
    if (!parsed.success) return null
    const { email, failed, outcome } = parsed.data

    if (outcome === 'ALREADY_ON_ACCOUNT' || outcome === 'NOT_ELIGIBLE') {
        // A failed check with no known outcome still deserves a warning.
        return failed && email
            ? { severity: 'warn', text: warning(email) }
            : null
    }
    if (failed) return { severity: 'warn', text: warning(email) }
    if (outcome === 'NEW_USER') {
        return {
            severity: 'info',
            text: `Portal invite sent to ${email}. They've been set up as an Admin user.`,
        }
    }
    return {
        severity: 'info',
        text: `${email} already had a portal login. They've been added to this account as an Admin and notified by email.`,
    }
}

const warning = (email: string) =>
    `Booking created, but we couldn't send the portal invite to ${email}`
